// src/pages/exercise/components/UI/ExerciseSuggestionPanel.jsx
import { createPortal } from 'react-dom';                          // ✅ moved to top
import { useStore } from '../Stores/exerciseStore';
import { CHARACTER_CONFIG } from '../../data/characterConfig';
import { useEffect, useRef, useState, useCallback } from 'react';
import { ConfettiCanvas } from './ConfettiCanvas';
import './ExerciseSuggestionPanel.css';


// ── Maps ──────────────────────────────────────────────────────────────────────
const INTENSITY_COLORS = {
  low:      '#6BAA75',
  moderate: '#C28A4B',
  high:     '#D4793A',
};

const TYPE_LABELS = {
  cardio:    'Cardio',
  strength:  'Strength',
  endurance: 'Endurance',
  power:     'Power',
  skill:     'Skill',
};

const MOTTOS_BY_TYPE = {
  cardio: [
    "Your lungs just got a little stronger.",
    "Every step forward is progress.",
    "Cardio is a conversation between you and your limits.",
    "You moved. That's what counts.",
    "Distance covered. Doubt reduced.",
  ],
  strength: [
    "Strength is built one rep at a time.",
    "You just got harder to stop.",
    "The weight didn't move itself.",
    "Earned, not given.",
    "Muscles remember what you did today.",
  ],
  endurance: [
    "Endurance is the quiet kind of brave.",
    "You stayed when it got uncomfortable.",
    "The body follows where the mind leads.",
    "Not fast. Not easy. Done.",
    "Consistency beats intensity every time.",
  ],
  power: [
    "Explosive effort. Real results.",
    "Power is just stored determination.",
    "Fast twitch. Sharp mind.",
    "You trained like it mattered. It does.",
    "That burst of effort? Your body logged it.",
  ],
  skill: [
    "Skill compounds. Keep showing up.",
    "Precision is its own kind of strength.",
    "You practiced. That's the whole game.",
    "Better than yesterday — that's the goal.",
    "Every rep teaches the body something new.",
  ],
};

const FALLBACK_MOTTOS = [
  "Done is better than perfect.",
  "Movement is medicine.",
  "You showed up. That matters.",
  "Progress lives in the reps you don't skip.",
  "One session closer.",
];

function getRandomMotto(trainingType) {
  const pool = MOTTOS_BY_TYPE[trainingType] ?? FALLBACK_MOTTOS;
  return pool[Math.floor(Math.random() * pool.length)];
}


// ── ExerciseCard ──────────────────────────────────────────────────────────────
function ExerciseCard({
  exercise,
  accentColor,
  index,
  isCompleted,
  rerollsLeft,
  onView,
  onReroll,
  onStart,
}) {
  const [isShaking, setIsShaking] = useState(false);
  const [motto]    = useState(() => getRandomMotto(exercise.trainingType));

  const handleReroll = useCallback(() => {
    if (rerollsLeft <= 0) return;
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 420);
    onReroll(index);
  }, [rerollsLeft, onReroll, index]);

  const cardClass = [
    'esp-card',
    isCompleted ? 'esp-card--done'  : '',
    isShaking   ? 'esp-card--shake' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={cardClass}>
      <div className="esp-card__top">
        <span className="esp-card__type" style={{ background: accentColor }}>
          {TYPE_LABELS[exercise.trainingType] ?? exercise.trainingType}
        </span>
        <div className="esp-card__top-right">
          <span
            className="esp-card__intensity-dot"
            style={{ background: INTENSITY_COLORS[exercise.intensity] ?? '#aaa' }}
            title={exercise.intensity}
          />
        </div>
      </div>

      <button className="esp-card__name-btn" onClick={() => onView(exercise)}>
        {isCompleted && <span className="esp-card__done-icon">&#10003; </span>}
        {exercise.name}
      </button>

      <div className="esp-card__footer">
        <span className="esp-card__chip">{exercise.duration}</span>
        <span className="esp-card__chip">{exercise.location.split(',')[0]}</span>
        <span className="esp-card__chip">{exercise.groupType.split(',')[0]}</span>
      </div>

      {!isCompleted && (
        <div className="esp-card__actions">
          <button
            className={`esp-card__btn esp-card__btn--reroll${
              rerollsLeft <= 0 ? ' esp-card__btn--disabled' : ''
            }`}
            onClick={handleReroll}
            disabled={rerollsLeft <= 0}
            title={rerollsLeft <= 0 ? 'No rerolls left today' : 'Swap this exercise'}
          >
            Reroll
          </button>
          <button
            className="esp-card__btn esp-card__btn--start"
            onClick={() => onStart(exercise, index)}
          >
            Start
          </button>
        </div>
      )}

      {isCompleted && (
        <p className="esp-card__motto">{motto}</p>
      )}
    </div>
  );
}


// ── ExerciseDetailDrawer ──────────────────────────────────────────────────────
function ExerciseDetailDrawer({ exercise, accentColor, onClose }) {
  // ✅ Hook is ALWAYS called — no early return before it
  useEffect(() => {
    if (!exercise) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [exercise, onClose]);

  // Guard AFTER hook
  if (!exercise || !exercise.name) return null;

  const hasFitt =
    exercise.fittFrequency ||
    exercise.fittIntensity ||
    exercise.fittTime      ||
    exercise.fittType;

  return createPortal(
    <div className="esp-drawer-backdrop" onClick={onClose}>
      <div
        className="esp-drawer"
        style={{ '--accent': accentColor }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="esp-drawer__close" onClick={onClose} aria-label="Close">
          &#x2715;
        </button>
        <h2 className="esp-drawer__title">{exercise.name}</h2>
        <span className="esp-drawer__badge" style={{ background: accentColor }}>
          {TYPE_LABELS[exercise.trainingType] ?? exercise.trainingType}
        </span>
        <p className="esp-drawer__desc">
          {exercise.description || 'No description available.'}
        </p>

        {hasFitt && (
          <div className="esp-drawer__fitt">
            <h3>FITT Principles</h3>
            <table>
              <tbody>
                {exercise.fittFrequency && <tr><td>Frequency</td><td>{exercise.fittFrequency}</td></tr>}
                {exercise.fittIntensity && <tr><td>Intensity</td><td>{exercise.fittIntensity}</td></tr>}
                {exercise.fittTime      && <tr><td>Time</td><td>{exercise.fittTime}</td></tr>}
                {exercise.fittType      && <tr><td>Type</td><td>{exercise.fittType}</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        <div className="esp-drawer__meta">
          <span>{exercise.duration}</span>
          <span>{exercise.equipment}</span>
          <span>{exercise.location}</span>
        </div>

        <div className="esp-drawer__links">
          {exercise.evidenceUrl && (
            <a href={exercise.evidenceUrl} target="_blank" rel="noreferrer">Evidence Source</a>
          )}
          {exercise.videoUrl && (
            <a href={exercise.videoUrl} target="_blank" rel="noreferrer">Watch Video</a>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}


// ── RerollCounter (named export) ──────────────────────────────────────────────
export function RerollCounter() {
  const rerollsLeft = useStore((s) => s.rerollsLeft);
  const isEmpty     = rerollsLeft === 0;
  return (
    <div className="esp-counter-external">
      <span className={`esp-reroll-pill${isEmpty ? ' esp-reroll-pill--empty' : ''}`}>
        Rerolls &nbsp;<strong>{rerollsLeft} / 2</strong>
      </span>
    </div>
  );
}


// ── Main ExerciseSuggestionPanel ──────────────────────────────────────────────
export function ExerciseSuggestionPanel({ onAllDone }) {

  const phase             = useStore((s) => s.phase);
  const charIndex         = useStore((s) => s.charIndex);
  const selectedExercise  = useStore((s) => s.selectedExercise);
  const missionPool       = useStore((s) => s.missionPool);
  const completedMissions = useStore((s) => s.completedMissions);
  const rerollsLeft       = useStore((s) => s.rerollsLeft);

  const {
    setSelectedExercise,
    clearSelectedExercise,
    rerollMission,
    startTimer,
    checkAndResetDailyRerolls,
  } = useStore.getState();

  const [visible, setVisible]               = useState(false);
  const [smallTrigger, setSmallTrigger]     = useState(0);
  const [confettiOrigin, setConfettiOrigin] = useState({ x: 0, y: 0 });

  const timerRef        = useRef(null);
  const seededRef       = useRef(false);
  const allDoneFiredRef = useRef(false);
  const cardRefs        = useRef([]);

  useEffect(() => {
    checkAndResetDailyRerolls();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (phase === 'TRANSITION1') {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setVisible(true), 1800);
    }
    if (phase === 'TRANSITION2' || phase === 'SELECTION') {
      if (timerRef.current) clearTimeout(timerRef.current);
      setVisible(false);
      seededRef.current       = false;
      allDoneFiredRef.current = false;
    }
    if (phase === 'EXERCISE') setVisible(true);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [phase]);

  useEffect(() => {
    if (visible && !seededRef.current) {
      useStore.getState().initMissions(charIndex);
      seededRef.current = true;
    }
  }, [visible, charIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      missionPool.length > 0 &&
      completedMissions.length === missionPool.length &&
      !allDoneFiredRef.current
    ) {
      allDoneFiredRef.current = true;
      onAllDone?.();
    }
  }, [completedMissions, missionPool, onAllDone]);

  const handleComplete = useCallback((missionIndex) => {
    useStore.getState().completeMission(missionIndex);
    const cardEl = cardRefs.current[missionIndex];
    if (cardEl) {
      const rect = cardEl.getBoundingClientRect();
      setConfettiOrigin({
        x: rect.left + rect.width  / 2,
        y: rect.top  + rect.height / 2,
      });
    } else {
      setConfettiOrigin({ x: window.innerWidth * 0.2, y: window.innerHeight * 0.5 });
    }
    setSmallTrigger((t) => t + 1);
  }, []);

  if (!visible) return null;

  const config  = CHARACTER_CONFIG[charIndex];
  const allDone = missionPool.length > 0 && completedMissions.length === missionPool.length;

  return (
    <>
      <div className="esp-root" onWheel={(e) => e.stopPropagation()}>

        <div className="esp-title-block">
          <h1 className="esp-title">Today's Exercise</h1>
          <p className="esp-subtitle">{config.emoji} {config.tagline}</p>
        </div>

        <div className="esp-divider" />

        <div className="esp-cards-row">
          {missionPool.length === 0 ? (
            <p className="esp-empty">Loading missions…</p>
          ) : (
            missionPool.map((ex, i) => (
              <div key={`${ex.id}-${i}`} ref={(el) => (cardRefs.current[i] = el)}>
                <ExerciseCard
                  exercise={ex}
                  accentColor={config.accentColor}
                  index={i}
                  isCompleted={completedMissions.includes(i)}
                  rerollsLeft={rerollsLeft}
                  onView={setSelectedExercise}
                  onReroll={(idx) => rerollMission(idx, charIndex)}
                  onStart={startTimer}
                />
              </div>
            ))
          )}
        </div>

        {allDone && (
          <div className="esp-allDone">
            <div className="esp-allDone__confetti">&#9733;</div>
            <p className="esp-allDone__msg">All done, {config.name}!</p>
            <p className="esp-allDone__sub">{config.tagline}</p>
          </div>
        )}

        <div className="esp-reroll-footer">
          <span className={`esp-reroll-pill${rerollsLeft === 0 ? ' esp-reroll-pill--empty' : ''}`}>
            Rerolls &nbsp;<strong>{rerollsLeft} / 2</strong>
          </span>
        </div>
      </div>

      {selectedExercise && (
        <ExerciseDetailDrawer
          exercise={selectedExercise}
          accentColor={config.accentColor}
          onClose={clearSelectedExercise}
        />
      )}

      <ConfettiCanvas
        trigger={smallTrigger}
        intensity="small"
        origin={confettiOrigin}
      />
    </>
  );
}